import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Paper, Stack, Typography, Button,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Chip,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import PrintIcon from "@mui/icons-material/Print";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import PaidIcon from "@mui/icons-material/Paid";


const normalizeBaseUrl = (raw) => {
  if (!raw) return "";
  const trimmed = raw.trim().replace(/\/$/, "");
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};
//! STATUSSSSSSSSSSSSSSSSSSSSSSS
const STATUS_CONFIG = {
  0: { label: "Unsubmitted", color: "default" },
  1: { label: "Pending", color: "warning" },
  2: { label: "Approved", color: "success" },
  3: { label: "Rejected", color: "error" },
  4: { label: "Paid", color: "success" },
  5: { label: "Partial", color: "info" },
};

// maps the same statusFilter values used in VisitApprovalPage ("pending",
// "approved", etc.) to the numeric status codes stored on each row
const STATUS_CODE = { pending: 1, partial: 5, approved: 2, rejected: 3, paid: 4 };

export default function VisitReportPage() {
  // this page has NO filter controls of its own — date range, employee,
  // and status all come from whatever was set on the Approve Visit
  // Requests page, passed here via navigate(..., { state }) when that
  // page's "Monthly Report" button is clicked.
  const location = useLocation();
  const navigate = useNavigate();
  const filter = location.state || {};

  const [visits, setVisits] = useState([]);
  const [totals, setTotals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [loadError, setLoadError] = useState("");

  const base = normalizeBaseUrl(localStorage.getItem("saved_sett_api_url") || "");

  // pulls both datasets for the incoming date range + employee, then
  // applies the incoming status filter client-side (the backend doesn't
  // take a status param — same approach VisitApprovalPage already uses)
    const generateReport = async (f) => {
    if (!f?.fromDate || !f?.toDate) return;
    setLoading(true);
    setLoadError("");
    try {
      // /api/visit/all and /api/visit/total/all only support a single
      // empId param on the backend — the Approve page now sends MULTIPLE
      // selected employees (empIds), so fetch everyone for the date
      // range and filter by employee client-side below, the same way
      // status filtering already works on this page.
      const params = { fromdate: f.fromDate, todate: f.toDate };

      const [visitsRes, totalsRes] = await Promise.all([
        axios.get(`${base}/api/visit/all`, { params }),
        axios.get(`${base}/api/visit/total/all`, { params }),
      ]);

      let visitList = Array.isArray(visitsRes.data) ? visitsRes.data : [];
      let totalList = Array.isArray(totalsRes.data) ? totalsRes.data : [];

      // employee filter — only applied if one or more employees were
      // selected on the Approve page; empty/missing means "everyone"
      if (Array.isArray(f.empIds) && f.empIds.length) {
        const idSet = new Set(f.empIds.map((id) => String(id)));
        visitList = visitList.filter((v) => idSet.has(String(v.empId)));
        totalList = totalList.filter((t) => idSet.has(String(t.empId)));
      }

      // "all" (or no status passed) means don't filter — otherwise only
      // keep rows matching the chosen status, same mapping as the Approve page
      if (f.status && f.status !== "all") {
        const code = STATUS_CODE[f.status];
        visitList = visitList.filter((v) => v.status === code);
        totalList = totalList.filter((t) => t.status === code);
      }

      const sortedVisits = visitList
        .slice()
        .sort((a, b) => String(a.fullName || a.empId).localeCompare(String(b.fullName || b.empId)));

      const sortedTotals = totalList
        .slice()
        .sort((a, b) => String(a.fullName || a.empId).localeCompare(String(b.fullName || b.empId)));

      setVisits(sortedVisits);
      setTotals(sortedTotals);
      setHasGenerated(true);
    } catch (e) {
      console.error("Failed to generate report:", e);
      setLoadError("Failed to load the report. Go back to Approve Visit Requests and click Monthly Report again.");
    } finally {
      setLoading(false);
    }
  };

  // auto-generate the moment we arrive, using whatever filter came along —
  // this page never asks the person to re-pick a date range, employee, or status
  useEffect(() => {
    if (filter.fromDate && filter.toDate) {
      generateReport(filter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => window.print();

  const rangeLabel =
    filter.fromDate && filter.toDate ? `${filter.fromDate} → ${filter.toDate}` : "";
  const statusLabel =
    filter.status && filter.status !== "all"
      ? STATUS_CONFIG[STATUS_CODE[filter.status]]?.label || filter.status
      : "All statuses";
  const empNamesLabel = Array.isArray(filter.empNames) && filter.empNames.length
    ? filter.empNames.join(", ")
    : "";
  const scopeLabel = [rangeLabel, empNamesLabel, statusLabel].filter(Boolean).join(" — ");

  const visitTotals = useMemo(() => ({
  amount: visits.reduce((sum, v) => sum + (Number(v.amount) || 0), 0),
  km: visits.reduce((sum, v) => sum + (Number(v.km) || 0), 0),
  parking: visits.reduce((sum, v) => sum + (Number(v.parking) || 0), 0),
}), [visits]);

const transportationTotals = useMemo(() => ({
  amount: totals.reduce((sum, t) => sum + (Number(t.amount) || 0), 0),
  km: totals.reduce((sum, t) => sum + (Number(t.km) || 0), 0),
  parking: totals.reduce((sum, t) => sum + (Number(t.parking) || 0), 0),
}), [totals]);

const renderTotalsSummary = (t) => (
  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ rowGap: 1, mb: 2 }}>
    <Chip icon={<PaidIcon />} size="small" label={`Total Amount: ${t.amount.toLocaleString()} L.L`}
      sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 500, fontSize: 17 , fontWeight:"bold"}} />
    <Chip icon={<PaidIcon />} size="small" label={`Total Kilometrage: ${t.km.toLocaleString()} Km`}
      sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 500, fontSize: 17 , fontWeight:"bold" }} />
    <Chip icon={<PaidIcon />} size="small" label={`Total Parking: ${t.parking.toLocaleString()} L.L`}
      sx={{ bgcolor: "#fff8e1", color: "#e65100", fontWeight: 500, fontSize: 17, fontWeight:"bold" }} />
  </Stack>
);

  return (
    <>
    <style>{`
      @media print {
        .no-print { display: none !important; }
        .page-break { page-break-before: always; }
        body { background: white !important; }
      }
    `}</style>

    <Box sx={{ minHeight: "100vh", p: { xs: 2, md: 3 }, display: "flex", justifyContent: "center" }}>
      <Paper elevation={10} sx={{ width: "100%", maxWidth: 1100, p: 3, borderRadius: 4 }}>
        {/* header — no filter controls here; this page reflects the Approve page's filter */}
        <Box className="no-print">
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <EventIcon sx={{ color: "#00796b" }} />
            <Typography variant="h6" fontWeight={800} color="#004d40">
              Visit Report
            </Typography>
          </Stack>

          {rangeLabel ? (
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Showing: <strong>{scopeLabel}</strong> — set from the Approve Visit Requests page.
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: "error.main", mb: 2 }}>
              No filter was passed in. Go back to Approve Visit Requests, set your date range / employee / status filter, then click "Monthly Report" again.
            </Typography>
          )}

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            {/* <Button variant="outlined" onClick={() => navigate(-1)}>
              ← Back to Approve Visit Requests
            </Button> */}

            <Button
              variant="contained"
              onClick={() => generateReport(filter)}
              disabled={loading || !filter.fromDate || !filter.toDate}
              sx={{ bgcolor: "#00796b" }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Refresh"}
            </Button>

            {hasGenerated && (
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
                Print / Save as PDF
              </Button>
            )}
          </Stack>

          {loadError && (
            <Typography variant="body2" sx={{ color: "error.main", mb: 2 }}>
              {loadError}
            </Typography>
          )}
        </Box>

        {hasGenerated && (
  <>
    {/* ── Visits table — page 1 when printed ── */}
    <Box className="print-section">
      <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#004d40", mb: 1.5 }}>
        Visits — {scopeLabel}
      </Typography>
      {renderTotalsSummary(visitTotals)}

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: "#004d40", color: "white", fontWeight: "bold" }}>Employee</TableCell>
              <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Visit From</TableCell>
              <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Visit To</TableCell>
              <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Date</TableCell>
              <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Amount (L.L)</TableCell>
              <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Km</TableCell>
              <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Parking (L.L)</TableCell>
              <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visits.map((v) => {
              const sc = STATUS_CONFIG[v.status] || STATUS_CONFIG[0];
              return (
                <TableRow key={v.entryNo}>
                  <TableCell>{v.fullName || v.empId}</TableCell>
                  <TableCell>{v.visitFrom}</TableCell>
                  <TableCell>{v.visitTo}</TableCell>
                  <TableCell>{v.date}</TableCell>
                  <TableCell>{Number(v.amount).toLocaleString()}</TableCell>
                  <TableCell>{v.km}</TableCell>
                  <TableCell>{Number(v.parking).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip label={sc.label} color={sc.color} size="small" variant="filled" />
                  </TableCell>
                </TableRow>
              );
            })}
            {!visits.length && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ opacity: 0.5, py: 3 }}>
                  No visits recorded for this filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>

    {/* ── Totals table — forced onto its own page when printed ── */}
    <Box className="print-section page-break">
      <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#004d40", mb: 1.5 }}>
        Total Transportation — {scopeLabel}
      </Typography>
      {renderTotalsSummary(transportationTotals)}

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: "#004d40", color: "white", fontWeight: "bold" }}>Employee</TableCell>
              <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Date Range</TableCell>
              <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Amount (L.L)</TableCell>
              <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Km</TableCell>
              <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Parking (L.L)</TableCell>
              <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {totals.map((t) => {
              const sc = STATUS_CONFIG[t.status] || STATUS_CONFIG[0];
              return (
                <TableRow key={t.entryNo}>
                  <TableCell>{t.fullName || t.empId}</TableCell>
                  <TableCell>{t.dateFrom} → {t.dateTo}</TableCell>
                  <TableCell>{Number(t.amount).toLocaleString()}</TableCell>
                  <TableCell>{t.km}</TableCell>
                  <TableCell>{Number(t.parking).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip label={sc.label} color={sc.color} size="small" variant="filled" />
                  </TableCell>
                </TableRow>
              );
            })}
            {!totals.length && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ opacity: 0.5, py: 3 }}>
                  No total transportation summaries for this filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  </>
)}
</Paper>
  </Box>
  </>
  );
}